import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Laptop, Loader2, Rocket, Save, Smartphone, Tablet, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errorText } from "@/lib/api";
import { useAdminLang } from "./context";
import { buildSections } from "./designerSections";

const ORIGIN = window.location.origin;
const DEVICES = { mobile: [390, 780, Smartphone], tablet: [820, 1000, Tablet], desktop: [1280, 820, Laptop] };

function setPath(obj, path, value) {
  const [head, ...rest] = path.split(".");
  return { ...obj, [head]: rest.length ? setPath(obj[head], rest.join("."), value) : value };
}

function PreviewFrame({ device, frameRef }) {
  const box = useRef(null);
  const [scale, setScale] = useState(1);
  const [w, h] = DEVICES[device];
  useLayoutEffect(() => {
    const ro = new ResizeObserver(([e]) => setScale(Math.min(1, (e.contentRect.width - 8) / w)));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, [w]);
  return (
    <div ref={box} className="w-full" style={{ height: h * scale + 8 }}>
      <div className="mx-auto overflow-hidden rounded-[22px] border-4 border-zinc-800 bg-black shadow-2xl" style={{ width: w * scale + 8, height: h * scale + 8 }}>
        <iframe ref={frameRef} title="preview" src="/preview" data-testid="designer-preview-frame" style={{ width: w, height: h, transform: `scale(${scale})`, transformOrigin: "0 0", border: 0 }} />
      </div>
    </div>
  );
}

export default function Designer() {
  const { t } = useAdminLang();
  const { refresh, stats } = useOutletContext();
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(null);
  const [device, setDevice] = useState("mobile");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const frame = useRef(null);
  const draftRef = useRef(null);
  draftRef.current = draft;

  const post = useCallback((msg) => frame.current?.contentWindow?.postMessage(msg, ORIGIN), []);
  const apply = (s) => {
    setDraft(s);
    setSaved(JSON.stringify(s));
  };

  useEffect(() => {
    api.get("/admin/settings").then((r) => apply(r.data.settings)).catch(() => toast.error(t("toast.loadFailed")));
    const onMsg = (e) => {
      if (e.origin === ORIGIN && e.data?.type === "wt-preview-ready" && draftRef.current) post({ type: "wt-preview-settings", settings: draftRef.current });
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [post, t]);

  useEffect(() => {
    if (draft) post({ type: "wt-preview-settings", settings: draft });
  }, [draft, post]);

  const set = useCallback((path, value) => setDraft((d) => setPath(d, path, value)), []);
  const unsaved = draft && JSON.stringify(draft) !== saved;

  const save = async () => {
    const r = await api.put("/admin/settings", draft);
    apply(r.data.settings);
    post({ type: "wt-preview-reload" });
    refresh();
  };

  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "save") {
        await save();
        toast.success(t("des.allSaved"));
      } else if (kind === "publish") {
        if (unsaved) await save();
        await api.post("/admin/publish");
        toast.success(t("toast.published"));
        refresh();
      } else {
        const r = await api.post("/admin/settings/discard");
        apply(r.data.settings);
        post({ type: "wt-preview-reload" });
        toast.success(t("des.discarded"));
        refresh();
      }
    } catch (e) {
      toast.error(errorText(e, t("toast.saveFailed")));
    } finally {
      setBusy(null);
    }
  };

  if (!draft) return <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const sections = buildSections(draft, set, t);

  return (
    <div className="space-y-6" data-testid="admin-designer-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">{t("des.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("des.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-xs font-semibold ${unsaved ? "text-amber-300" : "text-muted-foreground"}`} data-testid="designer-save-state">{unsaved ? t("des.unsaved") : t("des.allSaved")}</span>
          <Button variant="ghost" size="sm" onClick={() => setConfirmDiscard(true)} disabled={!!busy} data-testid="designer-discard-btn"><Undo2 className="h-4 w-4" />{t("des.discard")}</Button>
          <Button variant="secondary" size="sm" onClick={() => run("save")} disabled={!!busy || !unsaved} data-testid="designer-save-btn">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}{t("des.save")}</Button>
          <Button size="sm" onClick={() => run("publish")} disabled={!!busy || (!unsaved && !stats?.dirty)} data-testid="designer-publish-btn">{busy === "publish" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}{t("des.publish")}</Button>
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-[400px_1fr]">
        <Accordion type="multiple" defaultValue={["brand"]} className="space-y-2 lg:max-h-[calc(100vh-200px)] lg:overflow-y-auto lg:pe-2">
          {sections.map(([id, label, body]) => (
            <AccordionItem key={id} value={id} className="rounded-xl border bg-card px-4" data-testid={`designer-section-${id}`}>
              <AccordionTrigger className="text-sm font-semibold hover:no-underline" data-testid={`designer-section-${id}-trigger`}>{t(label)}</AccordionTrigger>
              <AccordionContent className="space-y-4 pb-4">{body}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="space-y-3 lg:sticky lg:top-20 lg:self-start">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-1 rounded-full border bg-card p-1" data-testid="device-switch">
              {Object.entries(DEVICES).map(([k, [, , Icon]]) => (
                <button key={k} type="button" onClick={() => setDevice(k)} aria-pressed={device === k} data-testid={`device-${k}`}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${device === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}><Icon className="h-3.5 w-3.5" />{t(`des.${k}`)}</button>
              ))}
            </div>
            <Button asChild variant="ghost" size="sm"><a href="/preview" target="_blank" rel="noreferrer" data-testid="designer-open-preview"><ExternalLink className="h-4 w-4" />{t("des.openPreview")}</a></Button>
          </div>
          <p className="text-[11px] text-muted-foreground">{t("des.previewNote")}</p>
          <PreviewFrame device={device} frameRef={frame} />
        </div>
      </div>
      <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <AlertDialogContent data-testid="discard-dialog">
          <AlertDialogHeader><AlertDialogTitle>{t("des.discardConfirm")}</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => run("discard")} data-testid="discard-confirm-btn">{t("des.discard")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
